import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Eye, QrCode, AlertCircle, CheckCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import AxiosDB from "@/Config/AxiosDB";

export default function ComprobantesRecibidos({ user, onLogout }) {
  const [comprobantes, setComprobantes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedComprobante, setSelectedComprobante] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    loadComprobantes();
  }, []);

  const loadComprobantes = async () => {
    try {
      const response = await AxiosDB.get(`/comprobantes/recibidos`);
      setComprobantes(response.data);
    } catch (error) {
      toast.error("Error al cargar comprobantes");
    } finally {
      setLoading(false);
    }
  };

  const getEstadoBadge = (estado) => {
    const estados = {
      valida: { variant: "default", className: "bg-green-100 text-green-800 border-green-200", icon: CheckCircle },
      invalida: { variant: "destructive", className: "bg-red-100 text-red-800 border-red-200", icon: AlertCircle },
      pendiente: { variant: "secondary", className: "bg-amber-100 text-amber-800 border-amber-200", icon: Clock },
    };
    return estados[estado] || estados.pendiente;
  };

  const getEstadoComprobacionBadge = (estado) => {
    const estados = {
      aprobado: { variant: "default", className: "bg-green-100 text-green-800 border-green-200" },
      rechazado: { variant: "destructive", className: "bg-red-100 text-red-800 border-red-200" },
      pendiente: { variant: "secondary", className: "bg-amber-100 text-amber-800 border-amber-200" },
    };
    return estados[estado] || estados.pendiente;
  };

  const getSeveridadBadge = (severidad) => {
    const severidades = {
      alta: "bg-red-100 text-red-800 border-red-200",
      media: "bg-amber-100 text-amber-800 border-amber-200",
      baja: "bg-blue-100 text-blue-800 border-blue-200",
    };
    return severidades[severidad] || severidades.baja;
  };

  const openModal = (comprobante) => {
    setSelectedComprobante(comprobante);
    setModalOpen(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: 'DOP'
    }).format(amount);
  };

  return (
    <Layout user={user} onLogout={onLogout} title="Comprobantes Recibidos">
      <Card data-testid="comprobantes-recibidos-card">
        <CardHeader>
          <CardTitle>Comprobantes Fiscales Recibidos</CardTitle>
          <CardDescription>
            Listado de todos los comprobantes fiscales que has recibido
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-600">Cargando...</div>
          ) : comprobantes.length === 0 ? (
            <div className="text-center py-8 text-slate-600">No hay comprobantes recibidos</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Fecha</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">NCF</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">RNC</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Nombre</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Monto</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">ITBIS</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-slate-700">Firma</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-slate-700">Estado</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-slate-700">Errores</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-slate-700">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {comprobantes.map((comp) => {
                    const estadoFirma = getEstadoBadge(comp.estado_firma);
                    const estadoComp = getEstadoComprobacionBadge(comp.estado_comprobacion);
                    const IconFirma = estadoFirma.icon;
                    return (
                      <tr key={comp.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`comprobante-row-${comp.id}`}>
                        <td className="py-3 px-4 text-sm">{comp.fecha_emision}</td>
                        <td className="py-3 px-4 text-sm font-mono">{comp.ncf}</td>
                        <td className="py-3 px-4 text-sm">{comp.rnc}</td>
                        <td className="py-3 px-4 text-sm">{comp.nombre}</td>
                        <td className="py-3 px-4 text-sm text-right font-semibold">{formatCurrency(comp.monto)}</td>
                        <td className="py-3 px-4 text-sm text-right">{formatCurrency(comp.itbis)}</td>
                        <td className="py-3 px-4 text-center">
                          <Badge className={estadoFirma.className}>
                            <IconFirma className="w-3 h-3 mr-1" />
                            {comp.estado_firma}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Badge className={estadoComp.className}>
                            {comp.estado_comprobacion}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-center">
                          {comp.errores.length > 0 ? (
                            <Badge variant="destructive" className="bg-red-100 text-red-800">
                              {comp.errores.length}
                            </Badge>
                          ) : (
                            <span className="text-slate-400 text-sm">-</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openModal(comp)}
                            data-testid={`view-details-${comp.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Ver
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="comprobante-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Detalle del Comprobante
            </DialogTitle>
            <DialogDescription>
              Información completa del comprobante fiscal
            </DialogDescription>
          </DialogHeader>
          {selectedComprobante && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-600">NCF</p>
                  <p className="text-base font-semibold font-mono" data-testid="modal-ncf">{selectedComprobante.ncf}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Fecha de Emisión</p>
                  <p className="text-base font-semibold" data-testid="modal-fecha">{selectedComprobante.fecha_emision}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">RNC</p>
                  <p className="text-base font-semibold" data-testid="modal-rnc">{selectedComprobante.rnc}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Nombre</p>
                  <p className="text-base font-semibold" data-testid="modal-nombre">{selectedComprobante.nombre}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Monto</p>
                  <p className="text-base font-semibold" data-testid="modal-monto">{formatCurrency(selectedComprobante.monto)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">ITBIS</p>
                  <p className="text-base font-semibold" data-testid="modal-itbis">{formatCurrency(selectedComprobante.itbis)}</p>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-sm text-slate-600">Estado de Firma</p>
                <Badge className={getEstadoBadge(selectedComprobante.estado_firma).className}>
                  {selectedComprobante.estado_firma}
                </Badge>
              </div>

              <div className="space-y-2">
                <p className="text-sm text-slate-600">Estado de Comprobación</p>
                <Badge className={getEstadoComprobacionBadge(selectedComprobante.estado_comprobacion).className}>
                  {selectedComprobante.estado_comprobacion}
                </Badge>
              </div>

              {selectedComprobante.errores.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-slate-700">Errores Detectados</p>
                  <div className="space-y-2">
                    {selectedComprobante.errores.map((error, index) => (
                      <div
                        key={index}
                        className="p-3 bg-slate-50 rounded-lg border border-slate-200"
                        data-testid={`error-item-${index}`}
                      >
                        <div className="flex items-start justify-between mb-1">
                          <span className="font-mono text-sm font-semibold">{error.codigo}</span>
                          <Badge className={getSeveridadBadge(error.severidad)}>
                            {error.severidad}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-700">{error.descripcion}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <p className="text-sm text-slate-600">Código QR</p>
                <div className="flex items-center gap-2">
                  <QrCode className="w-5 h-5 text-slate-600" />
                  <a
                    href={selectedComprobante.qr_data}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline text-sm"
                    data-testid="qr-link"
                  >
                    {selectedComprobante.qr_data}
                  </a>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}